/**
 * Tracking utility for capturing UTM parameters, landing page, and referrer.
 *
 * Behavior:
 *  - Captures on the FIRST page-load of a session (sessionStorage scoped).
 *  - If UTMs are present in the URL on a later page, refreshes them
 *    (so a paid-ad click in the same tab updates attribution).
 *  - Reads cleanly with `getTracking()` for inclusion in API submissions.
 *
 * NOTE: We intentionally use sessionStorage (not localStorage) so each
 * browsing session gets fresh attribution. UTMs typically reflect a single
 * marketing touch; carrying them across sessions would over-attribute.
 */

const STORAGE_KEY = 'snapTracking';

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

const safeParse = (raw) => {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const readStorage = () => {
  try {
    return safeParse(window.sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch (_) {
    return {};
  }
};

const writeStorage = (data) => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (err) {
    // Private mode / quota — best-effort, non-critical (UTM attribution only)
    console.warn('Failed to persist tracking data to sessionStorage:', err);
  }
};

/**
 * Call once on app boot. Idempotent — safe to call on every route change.
 * Captures UTMs from the URL the first time they appear, and locks in
 * landing_page + referrer the very first time a session is seen.
 */
export function captureTracking() {
  if (typeof window === 'undefined') return;

  const existing = readStorage();
  const params = new URLSearchParams(window.location.search);

  const incomingUtms = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) incomingUtms[k] = v;
  });

  const merged = { ...existing };

  // Landing + referrer: lock in once per session
  if (!merged.landing_page) {
    merged.landing_page = window.location.pathname + window.location.search;
  }
  if (!merged.referrer) {
    try {
      merged.referrer = document.referrer || '';
    } catch (_) {
      merged.referrer = '';
    }
  }
  if (!merged.first_seen_at) {
    merged.first_seen_at = new Date().toISOString();
  }

  // UTMs: refresh if any new ones are present in this URL
  if (Object.keys(incomingUtms).length > 0) {
    UTM_KEYS.forEach((k) => {
      merged[k] = incomingUtms[k] || merged[k] || '';
    });
    merged.last_utm_at = new Date().toISOString();
  } else {
    UTM_KEYS.forEach((k) => {
      if (merged[k] === undefined) merged[k] = '';
    });
  }

  // `src` is a custom short attribution param used by our own internal LPs
  // (e.g. ?src=cost_miami). Treat it like a UTM but separately.
  const src = params.get('src');
  if (src) merged.src = src;
  if (merged.src === undefined) merged.src = '';

  writeStorage(merged);
  return merged;
}

/**
 * Read the captured tracking blob. Always returns an object with the
 * standard keys present (empty strings if missing) so backend payloads
 * stay shape-stable.
 */
export function getTracking() {
  const raw = readStorage();
  const out = {
    utm_source: raw.utm_source || '',
    utm_medium: raw.utm_medium || '',
    utm_campaign: raw.utm_campaign || '',
    utm_content: raw.utm_content || '',
    utm_term: raw.utm_term || '',
    landing_page: raw.landing_page || '',
    referrer: raw.referrer || '',
    src: raw.src || '',
    first_seen_at: raw.first_seen_at || '',
  };
  return out;
}

/**
 * Clear tracking (used after successful lead submission to avoid
 * re-attributing a user's next quote to a stale UTM).
 */
export function clearTracking() {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.warn('Failed to clear tracking data from sessionStorage:', err);
  }
}
